import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { type ButtonHTMLAttributes, cloneElement, forwardRef, isValidElement, type ReactElement } from 'react';

const buttonVariants = cva(
    'cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-accent',
                destructive: 'border-destructive text-destructive hover:bg-destructive/90 hover:text-destructive-foreground/90',
                outline: 'border border-primary bg-background hover:bg-accent hover:text-accent-foreground',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                ghost: 'hover:bg-accent hover:text-accent-foreground',
                link: 'text-primary underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'h-9 rounded-md px-3',
                lg: 'h-11 rounded-md px-8',
                icon: 'h-10 w-10',
                small_icon: 'h-6 w-6',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
);

export interface ButtonProps
    extends ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild, children, ...props }, ref) => {
        if (asChild && isValidElement(children)) {
            const child = children as ReactElement<{ className?: string }>;

            return cloneElement(child, {
                ...props,
                ref,
                className: buttonVariants({ variant, size, className: clsx(className, child.props.className) }),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any);
        }

        return (
            <button
                className={ buttonVariants({ variant, size, className }) }
                ref={ ref }
                { ...props }
            >
                {children}
            </button>
        );
    }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
